import React, { useCallback, useEffect, useState } from 'react';
import { Download, Paperclip } from 'react-feather';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from 'reactstrap';
import {
  getFairAdminRequests,
  updateFairAdminRequest,
} from '../../ReduxToolkit/Reducers/fairManagement';

const formatSize = (bytes) => {
  if (!bytes) return '';
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// The uploads bucket allows cross-origin GETs, so the file is saved under its own name (a plain
// link would open a CSV in the tab). If the fetch fails, it opens in a new tab instead.
const downloadFile = async (file) => {
  try {
    const response = await fetch(file.url);
    if (!response.ok) throw new Error(response.statusText);
    const url = URL.createObjectURL(await response.blob());
    Object.assign(document.createElement('a'), { href: url, download: file.name }).click();
    URL.revokeObjectURL(url);
  } catch {
    window.open(file.url, '_blank', 'noopener');
  }
};

// One request's files (up to 10), each with its own Download button, plus Download all
const RequestFilesModal = ({ request, onClose }) => {
  // the url being downloaded, or 'all'
  const [downloading, setDownloading] = useState(null);
  const files = request?.files || [];

  const download = async (file) => {
    setDownloading(file.url);
    await downloadFile(file);
    setDownloading(null);
  };
  const downloadAll = async () => {
    setDownloading('all');
    for (const file of files) await downloadFile(file);
    setDownloading(null);
  };

  return (
    <Modal isOpen={!!request} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>
        Files from {request?.requested_by?.name || 'the fair'}
        <small className="d-block text-muted fw-normal">
          {request && new Date(request.created_at).toLocaleString()}
        </small>
      </ModalHeader>
      <ModalBody>
        <ListGroup flush>
          {files.map((file) => (
            <ListGroupItem
              key={file.url}
              className="d-flex align-items-center justify-content-between gap-3 px-0"
            >
              <div style={{ minWidth: 0 }}>
                <div className="text-break">{file.name}</div>
                {file.size ? <small className="text-muted">{formatSize(file.size)}</small> : null}
              </div>
              <Button
                size="sm"
                color="primary"
                outline
                className="d-flex align-items-center gap-1 flex-shrink-0"
                disabled={!!downloading}
                onClick={() => download(file)}
              >
                {downloading === file.url ? <Spinner size="sm" /> : <Download size={14} />}
                Download
              </Button>
            </ListGroupItem>
          ))}
        </ListGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" outline onClick={onClose}>
          Close
        </Button>
        {files.length > 1 && (
          <Button
            color="primary"
            className="d-flex align-items-center gap-1"
            disabled={!!downloading}
            onClick={downloadAll}
          >
            {downloading === 'all' ? <Spinner size="sm" color="light" /> : <Download size={14} />}
            Download all ({files.length})
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

// The fair's "Request To Admin" rows (sent from its Invited Profiles in the app). The admin
// downloads the files, fixes them into the upload shape, uploads them with a season's Upload CSV
// below, then marks the request completed. Status only: the fair isn't notified.
const FairAdminRequests = ({ organizationId }) => {
  const dispatch = useDispatch();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [filesFor, setFilesFor] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    const data = await dispatch(getFairAdminRequests(organizationId));
    setIsLoading(false);
    if (data) setRequests(data);
  }, [dispatch, organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (request, status) => {
    setUpdatingId(request.id);
    const result = await dispatch(updateFairAdminRequest(organizationId, request.id, status));
    setUpdatingId(null);
    if (result) {
      setRequests((current) =>
        current.map((r) => (r.id === request.id ? { ...r, status: result.status } : r))
      );
    }
  };

  const pending = requests.filter((r) => r.status === 'pending').length;

  return (
    <Card className="shadow-sm mb-4">
      <CardBody>
        <div className="d-flex align-items-center gap-2 mb-3">
          <h5 className="mb-0">Data requests</h5>
          {!!pending && <Badge color="warning">{pending} pending</Badge>}
        </div>
        {isLoading ? (
          <Spinner size="sm" />
        ) : !requests.length ? (
          <p className="text-muted mb-0">This fair hasn't sent any data requests.</p>
        ) : (
          <Table responsive className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Sent</th>
                <th>Requested by</th>
                <th>Files</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td className="text-nowrap">{new Date(request.created_at).toLocaleString()}</td>
                  <td>
                    {request.requested_by ? (
                      <>
                        <div>{request.requested_by.name || '--'}</div>
                        <small className="text-muted">{request.requested_by.email}</small>
                      </>
                    ) : (
                      <span className="text-muted">Removed member</span>
                    )}
                  </td>
                  <td>
                    {request.files.length ? (
                      <Button
                        size="sm"
                        color="primary"
                        outline
                        className="d-flex align-items-center gap-1 text-nowrap"
                        onClick={() => setFilesFor(request)}
                      >
                        <Paperclip size={14} />
                        {request.files.length} {request.files.length === 1 ? 'file' : 'files'}
                      </Button>
                    ) : (
                      <span className="text-muted">No files shared</span>
                    )}
                  </td>
                  <td>
                    <Badge color={request.status === 'completed' ? 'success' : 'warning'}>
                      {request.status === 'completed' ? 'Completed' : 'Pending'}
                    </Badge>
                  </td>
                  <td className="text-end text-nowrap">
                    <Button
                      size="sm"
                      color="primary"
                      outline={request.status === 'completed'}
                      disabled={updatingId === request.id}
                      onClick={() =>
                        setStatus(request, request.status === 'completed' ? 'pending' : 'completed')
                      }
                    >
                      {updatingId === request.id ? (
                        <Spinner size="sm" />
                      ) : request.status === 'completed' ? (
                        'Mark pending'
                      ) : (
                        'Mark completed'
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </CardBody>
      <RequestFilesModal request={filesFor} onClose={() => setFilesFor(null)} />
    </Card>
  );
};

export default FairAdminRequests;
